import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiFetch } from "@shared/client/api";
import { useT } from "@shared/client/locale";
import { useView } from "@shared/client/view";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Placeholder } from "@/components/page-states";
import { Switch } from "@shared/components/ui/switch";

type EmailPreferences = { weeklyDigest: boolean };

const VIEW = "/api/views/email-preferences";

/**
 * Les e-mails du produit. Il n'y en a que deux : l'accueil, envoyé une fois,
 * et le récapitulatif hebdomadaire — le seul qui se règle. Il part le lundi,
 * et seulement quand l'agent a trouvé du nouveau (shared/lib/product-emails.ts).
 *
 * Le choix s'écrit directement dans le cache de la vue (avant l'appel, puis
 * avec la réponse) : revenir sur les paramètres montre la dernière valeur,
 * jamais l'ancienne le temps d'une relecture.
 */
export function EmailNotifications() {
  const t = useT().settings.emailNotifications;
  const queryClient = useQueryClient();
  const { data, error: loadError } = useView<EmailPreferences>(VIEW);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save(next: boolean) {
    const previous = data;
    queryClient.setQueryData<EmailPreferences>([VIEW], { weeklyDigest: next });
    setBusy(true);
    setError("");
    try {
      const saved = await apiFetch<EmailPreferences>("/api/email-preferences", {
        method: "PATCH",
        body: JSON.stringify({ weeklyDigest: next }),
      });
      queryClient.setQueryData<EmailPreferences>([VIEW], saved);
    } catch (cause) {
      if (previous) queryClient.setQueryData<EmailPreferences>([VIEW], previous);
      setError(cause instanceof Error ? t.failedWithCause(cause.message) : t.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-2">
      <label className="flex items-center gap-3">
        {/* Pendant la lecture, un squelette : un interrupteur affiché « actif »
            puis basculé ferait croire à un réglage qui change tout seul. */}
        {data || loadError ? (
          <Switch
            aria-describedby="weekly-digest-help"
            aria-label={t.weeklyDigest}
            checked={data?.weeklyDigest ?? false}
            disabled={!data || busy}
            onCheckedChange={(next) => void save(next)}
          />
        ) : (
          <Placeholder className="h-5 w-9 rounded-full" />
        )}
        <span className="text-[13px] font-semibold">{t.weeklyDigest}</span>
      </label>
      <p className="m-0 text-[12.5px] text-muted-foreground" id="weekly-digest-help">
        {t.weeklyDigestHelp}
      </p>
      {(error || loadError) && (
        <Alert variant="destructive">
          <AlertDescription>{error || t.loadFailed}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
